import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiSearch, FiHelpCircle, FiBook, FiVideo, FiDownload, FiMessageSquare, FiPlus, FiMinus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import MetaTags from '../components/SEO/MetaTags';

const SupportCategory = ({ icon, title, description, articles, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="glass-card p-6 rounded-xl"
    >
      <div className="flex items-start">
        <div className="text-primary-600 text-2xl mr-4 mt-1">
          {icon}
        </div>
        <div className="flex-grow">
          <h3 className="text-xl font-semibold mb-2">{title}</h3>
          <p className="text-neutral-dark/80 mb-4">{description}</p>
          
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center text-primary-600 hover:text-primary-700 transition-colors mb-4"
          >
            {isOpen ? (
              <>
                <FiMinus className="mr-1" /> Hide Articles
              </>
            ) : (
              <>
                <FiPlus className="mr-1" /> Show Articles ({articles.length})
              </>
            )}
          </button>
          
          {isOpen && (
            <motion.ul
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
              className="space-y-2 ml-1"
            >
              {articles.map((article, idx) => (
                <li key={idx}>
                  <a href={`/support/article/${article.slug}`} className="text-neutral-dark/90 hover:text-primary-600 transition-colors">
                    {article.title}
                  </a>
                </li>
              ))}
            </motion.ul>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Support = () => {
  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const supportCategories = [
    {
      icon: <FiHelpCircle />,
      title: "Getting Started",
      description: "Everything you need to know to set up and start using your Ainous.",
      articles: [
        { title: "Unboxing and Initial Setup", slug: "unboxing-initial-setup" },
        { title: "Connecting to Wi-Fi", slug: "connecting-wifi" },
        { title: "Creating Your Account", slug: "creating-account" },
        { title: "Basic Voice Commands", slug: "basic-voice-commands" },
        { title: "Understanding Ainous Status Indicators", slug: "status-indicators" }
      ]
    },
    {
      icon: <FiBook />,
      title: "User Guides",
      description: "Detailed guides on Ainous features and capabilities.",
      articles: [
        { title: "Home Automation Integration", slug: "home-automation" },
        { title: "Calendar and Reminder Management", slug: "calendar-reminders" },
        { title: "Customizing Your Ainous Personality", slug: "customize-personality" },
        { title: "Using Ainous for Productivity", slug: "productivity" },
        { title: "Ainous Media and Entertainment Features", slug: "media-entertainment" }
      ]
    },
    {
      icon: <FiVideo />,
      title: "Video Tutorials",
      description: "Visual guides for getting the most out of your Ainous assistant.",
      articles: [
        { title: "Video: Setting Up Your Ainous", slug: "video-setup" },
        { title: "Video: Advanced Voice Commands", slug: "video-advanced-commands" },
        { title: "Video: Customizing Your Experience", slug: "video-customization" },
        { title: "Video: Integration with Smart Home", slug: "video-smart-home" },
        { title: "Video: Troubleshooting Common Issues", slug: "video-troubleshooting" }
      ]
    },
    {
      icon: <FiDownload />,
      title: "Updates & Downloads",
      description: "Latest software updates and additional resources.",
      articles: [
        { title: "Current Software Version Information", slug: "current-version" },
        { title: "How to Update Your Ainous", slug: "how-to-update" },
        { title: "Beta Program Enrollment", slug: "beta-program" },
        { title: "Release Notes and Change Logs", slug: "release-notes" },
        { title: "Additional Voice Packs", slug: "voice-packs" }
      ]
    },
    {
      icon: <FiMessageSquare />,
      title: "Troubleshooting",
      description: "Solutions for common issues and questions.",
      articles: [
        { title: "Ainous Won't Power On", slug: "power-issues" },
        { title: "Wi-Fi Connection Problems", slug: "wifi-problems" },
        { title: "Voice Recognition Issues", slug: "voice-recognition" },
        { title: "Battery Life Optimization", slug: "battery-life" },
        { title: "Factory Reset Instructions", slug: "factory-reset" }
      ]
    }
  ];

  // Frequently asked questions
  const faqs = [
    {
      question: "How long does the Ainous battery last?",
      answer: "The Ainous battery typically lasts about 18 hours with normal usage. When battery is low, Ainous will automatically return to its charging dock."
    },
    {
      question: "Is my data secure with Ainous?",
      answer: "Yes, we take data security seriously. Ainous processes most data locally on the device, and all cloud communications are end-to-end encrypted. You have complete control over what information is stored and shared."
    },
    {
      question: "Can Ainous understand multiple languages?",
      answer: "Yes, Ainous currently supports English, Spanish, French, German, Japanese, and Mandarin Chinese, with more languages being added regularly through software updates."
    },
    {
      question: "How do I expand Ainous's capabilities?",
      answer: "Ainous supports a growing ecosystem of skills and integrations that can be added through the companion app. You can browse the skill marketplace to add new functionalities."
    },
    {
      question: "What's the warranty period for Ainous?",
      answer: "The Essential model comes with a 1-year hardware warranty, the Premium model with a 2-year comprehensive warranty, and the Family model with a 3-year comprehensive warranty."
    }
  ];

  return (
    <div className="min-h-screen">
      <MetaTags
        title="Support & Knowledge Base | xdimension"
        description="Get help with your Ainous AI assistant. Browse our knowledge base, video tutorials, and troubleshooting guides."
        url="https://xdimension.ai/support"
      />

      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8">
            <FiArrowLeft className="mr-2" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">Support & Knowledge Base</h1>
            <p className="text-xl text-neutral-dark/80 max-w-3xl mx-auto">
              Find answers to your questions and learn how to get the most out of your Ainous AI assistant.
            </p>
          </motion.div>
          
          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-3xl mx-auto mb-16"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Search for answers..."
                className="w-full p-4 pl-12 rounded-xl bg-white/80 border border-neutral-light/20 focus:outline-none focus:ring-2 focus:ring-primary-500 text-lg"
              />
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-dark/50 text-xl" />
            </div>
          </motion.div>
          
          {/* Support Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {supportCategories.map((category, index) => (
              <SupportCategory key={index} {...category} index={index} />
            ))}
          </div>
          
          {/* FAQs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="glass-card p-8 md:p-10 rounded-xl mb-16"
          >
            <h2 className="text-2xl font-display font-bold mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 + (index * 0.1) }}
                  className="border-b border-neutral-light/20 pb-4 last:border-b-0"
                >
                  <h4 className="text-lg font-semibold mb-2">{faq.question}</h4>
                  <p className="text-neutral-dark/80">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Contact Support */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="glass-card p-8 md:p-10 rounded-xl text-center"
          >
            <h2 className="text-2xl font-display font-bold mb-4">Still Need Help?</h2>
            <p className="text-lg text-neutral-dark/80 mb-6">
              Our support team is ready to assist you with any questions or issues you may have.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="mailto:support@xdimension.ai" 
                className="btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = 'mailto:support@xdimension.ai?subject=Ainous Support Request';
                }}
              >
                Contact Support
              </a>
            </div>
            <p className="mt-6 text-sm text-neutral-dark/60">
              Support Hours: Monday-Friday, 9am-8pm ET | Saturday-Sunday, 10am-6pm ET
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Support;
